/**
 * Unit conversion utilities for weather data
 *
 * Database stores forecast data in metric units (mm for snowfall, Celsius for temperature)
 * Report data (from ski resorts/snotels) is already in imperial units (inches, Fahrenheit)
 *
 * These utilities convert forecast data for display, with support for future user preferences
 */

import { isNumberOrZero } from './core-utils'

/**
 * Unit preference type
 * Currently: defaults to 'imperial' for all users
 * Future: will be a user setting in preferences
 */
export type UnitPreference = 'imperial' | 'metric'

/**
 * Convert millimeters to inches
 * Matches database conversion logic: mm / 25.4
 */
export function mmToInches(mm: number): number {
  return mm / 25.4
}

/**
 * Convert Celsius to Fahrenheit
 * Matches database conversion logic: celsius * 9/5 + 32
 */
export function cToF(celsius: number): number {
  return celsius * (9 / 5) + 32
}

/**
 * Convert kilometers per hour to miles per hour
 * Conversion factor: 1 km = 0.621371 miles
 */
export function kmhToMph(kmh: number): number {
  return kmh * 0.621371
}

/**
 * Round a number to specified decimal places
 *
 * @param value - The number to round
 * @param decimals - Number of decimal places (default: 1)
 * @returns Rounded number
 */
export function round(value: number, decimals = 1): number {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

/**
 * Safely round a number that might be null/undefined
 * Treats 0 as truthy (will round 0 to 0, not return null)
 *
 * @param value - The number to round (can be null/undefined)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Rounded number or null if value is not a number
 */
export function safeRound(
  value: number | null | undefined,
  decimals = 1,
): number | null {
  return isNumberOrZero(value) ? round(value, decimals) : null
}

/**
 * Convert snowfall measurement from mm to inches (or keep as mm)
 * Handles null/undefined values and rounds result
 *
 * @param mm - Snowfall in millimeters
 * @param units - Target unit preference (default: 'imperial')
 * @returns Converted and rounded snowfall, or null if input is null/undefined
 *
 * @example
 * convertSnowfall(25.4, 'imperial') // => 1.0 (inches)
 * convertSnowfall(25.4, 'metric')   // => 25.4 (mm)
 * convertSnowfall(null, 'imperial') // => null
 * convertSnowfall(0, 'imperial')    // => 0 (treats 0 as valid)
 */
export function convertSnowfall(
  mm: number | null | undefined,
  units: UnitPreference = 'imperial',
): number | null {
  if (!isNumberOrZero(mm)) return null
  const converted = units === 'imperial' ? mmToInches(mm) : mm
  return round(converted, 1)
}

/**
 * Convert temperature from Celsius to Fahrenheit (or keep as Celsius)
 * Handles null/undefined values and rounds result
 *
 * @param celsius - Temperature in Celsius
 * @param units - Target unit preference (default: 'imperial')
 * @returns Converted and rounded temperature, or null if input is null/undefined
 *
 * @example
 * convertTemp(0, 'imperial')      // => 32.0 (°F)
 * convertTemp(0, 'metric')        // => 0.0 (°C)
 * convertTemp(null, 'imperial')   // => null
 * convertTemp(-10, 'imperial')    // => 14.0 (treats negative as valid)
 */
export function convertTemp(
  celsius: number | null | undefined,
  units: UnitPreference = 'imperial',
): number | null {
  if (!isNumberOrZero(celsius)) return null
  const converted = units === 'imperial' ? cToF(celsius) : celsius
  return round(converted, 1)
}

/**
 * Convert wind speed from km/h to mph (or keep as km/h)
 * Handles null/undefined values and rounds result
 *
 * @param kmh - Wind speed in kilometers per hour
 * @param units - Target unit preference (default: 'imperial')
 * @returns Converted and rounded wind speed, or null if input is null/undefined
 *
 * @example
 * convertWindSpeed(100, 'imperial')   // => 62.1 (mph)
 * convertWindSpeed(100, 'metric')     // => 100.0 (km/h)
 * convertWindSpeed(null, 'imperial')  // => null
 * convertWindSpeed(0, 'imperial')     // => 0 (treats 0 as valid)
 */
export function convertWindSpeed(
  kmh: number | null | undefined,
  units: UnitPreference = 'imperial',
): number | null {
  if (!isNumberOrZero(kmh)) return null
  const converted = units === 'imperial' ? kmhToMph(kmh) : kmh
  return round(converted, 1)
}
